import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  BookOpenIcon,
  HeartIcon,
  ShieldCheckIcon,
  UsersIcon,
  AcademicCapIcon,
  StarIcon
} from '@heroicons/react/24/outline';

const WhyAureus = () => {
  const features = [
    {
      title: "Cambridge Curriculum with Islamic Integration",
      description: "Your child benefits from a globally respected academic programme while staying connected to their Islamic roots through daily Tarbiyah and Quranic learning.",
      icon: AcademicCapIcon
    },
    {
      title: "Tarbiyah Infused Environment",
      description: "We don't just teach subjects, we cultivate adab (manners), akhlaq (character) and a love for Allah ﷻ through structured guidance, morning duas and value based stories.",
      icon: HeartIcon
    },
    {
      title: "Balanced Focus: Dunyah & Akhirah",
      description: "Whether it's learning English phonics or memorising the Quran, we maintain a holistic balance so your child grows intellectually, emotionally and spiritually.",
      icon: StarIcon
    },
    {
      title: "Optional Hifz Stream",
      description: "A dedicated path for students interested in Hifz from Grade 3 onwards, offered alongside core academics so memorisation never comes at the cost of learning.",
      icon: BookOpenIcon
    },
    {
      title: "Secure, Child Centric Campus",
      description: "Our school is designed for safety, comfort and exploration, from age appropriate classrooms to outdoor learning zones. Clean, welcoming and spiritually uplifting.",
      icon: ShieldCheckIcon
    },
    {
      title: "Parental Involvement and Transparency",
      description: "We view parents as partners. Regular updates, open communication and involvement in key milestones ensure you are always part of your child's journey.",
      icon: UsersIcon
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative flex items-center justify-center min-h-screen overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={require('../../assets/why aureus.png')}
            alt="Why Aureus"
            className="object-cover w-full h-full"
          />
        </div>
        <div className="absolute inset-0 bg-black/60" />

        <div className="relative z-10 max-w-4xl px-4 mx-auto text-center text-white">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-2 text-base font-semibold tracking-widest text-white uppercase md:text-lg"
            style={{ letterSpacing: '0.18em' }}
          >
            Discover Our Difference
          </motion.p>
          <motion.h1
            className="mb-6 text-5xl font-bold md:text-7xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            WHY <span className="text-yellow-400">AUREUS</span>?
          </motion.h1>
          <motion.p
            className="mb-8 text-xl text-gray-200 md:text-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Where values shape vision and excellence meets compassion
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <Link
              to="/apply-online"
              className="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-semibold text-black transition-colors duration-300 bg-yellow-400 rounded-full sm:w-auto hover:bg-yellow-500 group"
            >
              Apply Now
              <ChevronRightIcon className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/not-found"
              className="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-semibold text-black transition-all duration-300 bg-yellow-400 border-2 border-yellow-400 rounded-full shadow-lg sm:w-auto hover:bg-yellow-500 hover:border-yellow-500"
            >
              Schedule a Visit
            </Link>
          </motion.div>
        </div>
      </section>

      {/* What Sets Us Apart Section */}
      <section className="py-20 bg-slate-50">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-3xl font-bold md:text-4xl text-slate-900">
              What Sets Us Apart
            </h2>
            <p className="max-w-3xl mx-auto text-lg text-slate-600">
              Discover the unique approach that makes Aureus Cambridge School special
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-8 transition-all duration-300 bg-white shadow-lg rounded-2xl hover:shadow-xl group"
              >
                <div className="flex items-center justify-center w-16 h-16 mb-6 transition-transform bg-yellow-400 rounded-xl group-hover:scale-110">
                  <feature.icon className="w-8 h-8 text-black" />
                </div>
                <h3 className="mb-4 text-xl font-bold text-slate-900">{feature.title}</h3>
                <p className="leading-relaxed text-slate-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quranic Inspiration Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl px-4 mx-auto text-center sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <BookOpenIcon className="w-16 h-16 mx-auto mb-6 text-yellow-400" />
            <h2 className="mb-8 text-3xl font-bold text-white md:text-4xl">
              Quranic Inspiration
            </h2>

            <div className="p-8 bg-white/10 rounded-2xl backdrop-blur-sm">
              <p className="mb-4 text-2xl font-bold leading-relaxed text-white md:text-3xl">
                "A community which guides by truth and thereby establishes justice."
              </p>
              <p className="text-lg font-semibold text-yellow-400">
                — Surah Al-A'raf 7:181
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/*  */}

      {/* Call to Action */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-4xl px-4 mx-auto text-center sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="mb-6 text-3xl font-bold md:text-4xl text-slate-900">
              Ready to Experience the Aureus Difference?
            </h2>
            <p className="max-w-2xl mx-auto mb-8 text-xl text-slate-800">
              Join our community where your child's academic and spiritual growth go hand in hand
            </p>

            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a
                href="/apply-online"
                className="flex items-center justify-center w-full px-8 py-4 text-lg font-semibold text-white no-underline transition-all duration-300 rounded-full sm:w-auto bg-slate-900 hover:bg-slate-800"
              >
                Apply for Admission
              </a>
              <a
                href="/contact"
                className="flex items-center justify-center w-full px-8 py-4 text-lg font-semibold no-underline transition-all duration-300 border-2 rounded-full sm:w-auto border-slate-900 hover:bg-slate-900 hover:text-white text-slate-900"
              >
                Schedule a Visit
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WhyAureus;
